import { Countries } from "./countries";
import { CountryCode, Language } from "./enum";
import { VatBasic } from "./interfaces";
import {
  belgiumIBANPattern,
  belgiumZipCodePattern,
  dutchIBANPattern,
  dutchZipCodePattern,
  turkiyeZipCodePattern,
  turkiyeIBANPattern,
} from "./patterns";
import { filterPaymentByCountry, Payment } from "./payments";
import { filterSourceByCountry, Source } from "./sources";

export type AddressFields =
  | "zipcode"
  | "houseNumber"
  | "houseNumberExtension"
  | "area"
  | "city"
  | "street"
  | "district"
  | "neighborhood"
  | "floor";

export interface CountryConfig {
  currency: {
    code: string;
    symbol: string;
    position: string;
    hasDifferentSymbol?: boolean;
  };
  address: {
    fields: AddressFields[][][];
    zipCodePattern: RegExp;
    zipCodeMaxLength: number;
    zipCodePlaceholder: string;
  };
  dateFormat: string;
  ibanPattern: RegExp;
  countryCode:
    | CountryCode.NETHERLANDS
    | CountryCode.BELGIUM
    | CountryCode.TURKIYE;
  locale: "nl" | "en" | "tr";
  phone: { prefix: string; pattern: RegExp };
  vats: VatBasic[];

  payments: Payment[];
  sources: Source[];
  phonePrefix: string;
  // digits allowed after the phone prefix
  phoneLength: number;
  language: number;
}

export const countryConfig: Record<Countries, CountryConfig> = {
  NETHERLANDS: {
    locale: "nl",
    countryCode: CountryCode.NETHERLANDS,
    currency: { code: "EUR", symbol: "€", position: "before" },
    address: {
      fields: [
        [["zipcode"], ["houseNumber", "houseNumberExtension"]],
        [["street"], ["city"]],
      ],
      zipCodePattern: dutchZipCodePattern,
      zipCodeMaxLength: 6,
      zipCodePlaceholder: "1234AA",
    },
    dateFormat: "DD-MM-YYYY",
    ibanPattern: dutchIBANPattern,
    phone: { prefix: "+31", pattern: /^06\d{8}$/ },
    vats: [
      { id: 1, label: "vats.vatNlNone", percentage: 0 },
      { id: 2, label: "vats.vatNlLow", percentage: 9 },
      { id: 3, label: "vats.vatNlHigh", percentage: 21 },
    ],
    payments: filterPaymentByCountry(CountryCode.NETHERLANDS),
    sources: filterSourceByCountry(CountryCode.NETHERLANDS),
    phonePrefix: "+31",
    phoneLength: 9,
    language: Language.DUTCH,
  },
  BELGIUM: {
    locale: "nl",
    countryCode: CountryCode.BELGIUM,
    currency: { code: "EUR", symbol: "€", position: "before" },
    address: {
      fields: [
        [["zipcode"], ["area", "city"]],
        [["street"], ["houseNumber", "houseNumberExtension"]],
      ],
      zipCodePattern: belgiumZipCodePattern,
      zipCodeMaxLength: 4,
      zipCodePlaceholder: "1234",
    },
    dateFormat: "DD/MM/YYYY",
    ibanPattern: belgiumIBANPattern,
    phone: { prefix: "+31", pattern: /^06\d{8}$/ }, //?
    vats: [
      { id: 6, label: "vats.vatBeNone", percentage: 0 },
      { id: 7, label: "vats.vatBeLow", percentage: 6 },
      { id: 8, label: "vats.vatBeHigh", percentage: 12 },
      { id: 9, label: "vats.vatBeStandard", percentage: 21 },
    ],
    payments: filterPaymentByCountry(CountryCode.BELGIUM),
    sources: filterSourceByCountry(CountryCode.BELGIUM),
    phonePrefix: "+32",
    phoneLength: 9,
    language: Language.DUTCH,
  },
  TURKIYE: {
    locale: "tr",
    countryCode: CountryCode.TURKIYE,
    currency: {
      code: "TRY",
      symbol: "TL",
      position: "after",
      hasDifferentSymbol: true,
    },
    address: {
      fields: [
        [["city"], ["district"]],
        [["neighborhood"], ["street"], ["zipcode"]],
        [["houseNumber"], ["houseNumberExtension"], ["floor"]],
      ],
      zipCodePattern: turkiyeZipCodePattern,
      zipCodeMaxLength: 5,
      zipCodePlaceholder: "12345",
    },
    dateFormat: "DD.MM.YYYY",
    ibanPattern: turkiyeIBANPattern,
    phone: { prefix: "+90", pattern: /^0\d{10}$/ },
    vats: [
      { id: 10, label: "vats.vatTrLow", percentage: 1 },
      { id: 11, label: "vats.vatTrStandard", percentage: 10 },
    ],
    payments: filterPaymentByCountry(CountryCode.TURKIYE),
    sources: filterSourceByCountry(CountryCode.TURKIYE),
    phonePrefix: "+90",
    phoneLength: 10,
    language: Language.TURKISH,
  },
};
